import { batch, computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { IdeaDTO } from 'src/dtos/Idea';
import { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import { ScannerPlanDTO } from 'src/features/scanner/dtos/ScannerPlanDTO';
import { ScreenerPlanDTO } from 'src/features/screener/dtos/ScreenerPlanDTO';
import { WatchlistPlanDTO } from 'src/features/watchlist/dtos/WatchlistPlanDTO';
import type { SlowIdeaSource } from 'src/types/slowIdeaSource';

export interface TimeListIdeaSettings {
    universeId: string | null;
    slowIdeaKind?: 'universe' | 'idea';
    fastIdeasIds: string[];
}
export type FastIdea = IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>;
export type SlowIdeaInput =
    | IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO>
    | { universeId: string }
    | { type: string; id: string };

function isFastIdea(idea: IdeaDTO): idea is FastIdea {
    return idea.plan instanceof ScannerPlanDTO || idea.plan instanceof SearchPlanDTO;
}
function isSlowIdea(idea: IdeaDTO): idea is IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO> {
    return idea.plan instanceof ScreenerPlanDTO || idea.plan instanceof WatchlistPlanDTO;
}

/** Resolved data is cached by ID; the selected IDs have exactly one owner. */
export class TimeListIdeaSelection implements Disposable {
    readonly #cache = signal<ReadonlyMap<string, IdeaDTO>>(new Map());
    #disposed = false;
    #slowSelectionVersion = 0;
    readonly slowSource: ReadonlySignal<SlowIdeaSource | null>;
    readonly fastIdeas: ReadonlySignal<FastIdea[]>;

    constructor(
        readonly settings: ReadonlySignal<TimeListIdeaSettings>,
        readonly update: (selection: Partial<TimeListIdeaSettings>) => void,
        readonly initialize: (selection: Partial<TimeListIdeaSettings>) => void,
        readonly loadIdea: (id: string) => Promise<IdeaDTO>,
        readonly loadDefaultUniverse: () => Promise<string>,
    ) {
        this.slowSource = computed(() => {
            const { universeId: id, slowIdeaKind } = settings.value;
            if (!id) return null;
            const dto = this.#cache.value.get(id);
            return slowIdeaKind !== 'universe' && dto && isSlowIdea(dto)
                ? { type: 'idea', id, dto }
                : { type: 'universe', id };
        });
        this.fastIdeas = computed(() =>
            settings.value.fastIdeasIds.flatMap((id) => {
                const dto = this.#cache.value.get(id);
                return dto && isFastIdea(dto) ? [dto] : [];
            }),
        );
    }

    #remember(ideas: readonly IdeaDTO[], overwrite: boolean): void {
        if (this.#disposed) return;
        const cache = new Map(this.#cache.peek());
        for (const idea of ideas) {
            const id = idea.idea.id;
            if (id && (overwrite || !cache.has(id))) cache.set(id, idea);
        }
        this.#cache.value = cache;
    }

    async loadInitial(): Promise<void> {
        const initial = this.settings.peek();
        const version = this.#slowSelectionVersion;
        if (initial.universeId === null) {
            const id = await this.loadDefaultUniverse();
            if (this.#disposed) return;
            if (version === this.#slowSelectionVersion) this.initialize({ universeId: id, slowIdeaKind: 'universe' });
        }
        const ids = [...(initial.universeId ? [initial.universeId] : []), ...initial.fastIdeasIds];
        const loaded = await Promise.all(ids.map((id) => this.loadIdea(id).catch(() => null)));
        this.#remember(
            loaded.filter((idea): idea is IdeaDTO => idea !== null),
            false,
        );
    }

    selectSlow(ideas: readonly SlowIdeaInput[]): void {
        if (this.#disposed) return;
        this.#slowSelectionVersion++;
        const idea = ideas[0];
        batch(() => {
            if (!idea) this.update({ universeId: null, slowIdeaKind: undefined });
            else if ('idea' in idea) {
                this.#remember([idea], true);
                this.update({ universeId: idea.idea.id ?? null, slowIdeaKind: 'idea' });
            } else {
                this.update({ universeId: 'universeId' in idea ? idea.universeId : idea.id, slowIdeaKind: 'universe' });
            }
        });
    }

    selectFast(ideas: readonly FastIdea[]): void {
        if (this.#disposed) return;
        batch(() => {
            this.#remember(ideas, true);
            this.update({ fastIdeasIds: ideas.flatMap((idea) => (idea.idea.id ? [idea.idea.id] : [])) });
        });
    }

    [Symbol.dispose](): void {
        this.#disposed = true;
    }
}
